<!--
  NavigationDrawer.vue — main app sidebar (rail + expand-on-hover).

  Renders from the declarative config in config/navGroups.js: "subheader"
  section labels (Data entry / Data review / Modules), standalone "leaf" items
  (single click target, e.g. Home, Assets) and "group" items (a parent row +
  children once the rail drawer is hover-expanded, e.g. GL → Ledger/COA/...).

  Two drawer states — no hover auto-expand (Maurizio, 2026-09-09). The app-bar
  button (☰ ↔ ✕) toggles them; a click anywhere outside the expanded drawer
  also collapses it (2026-09-10):
  - collapsed: on desktop an icon rail that is always present, every item
    carrying its title as a tooltip; a section label shows as a thin divider;
    children are not in the DOM. On mobile, collapsed means hidden.
  - expanded: full width with labels; the group containing the CURRENT route
    shows its children (indented, Vuetify `ps-4`), plus any group the user
    opened by hand.

  One rule for every group header (Warehouse, GL, Sales, System Settings, and
  the path-less accordions Daily/Monthly operations alike): collapsed → click
  expands it and lands on the group's page; expanded → click collapses it —
  EXCEPT that a header clicked while you are on one of its OTHER pages takes
  you back to its page and stays open (Maurizio, 2026-09-10: from Orders a
  click on Sales must return to Sales Analytics — the header is the module's
  front door, and its subtitle names that page). A group's page is its own
  (Warehouse → Inventory) or, for a path-less accordion, its LEAD entry: the
  first child this user can see (2026-09-11 — Delivery for a warehouse admin,
  Stock Out for a kitchen operator, Month End or Payroll for Monthly
  operations); the accordion's subtitle follows the same rule. From the rail a click expands the drawer and opens the group. The
  per-group override (manualOpen) wins over the route in both directions and is
  cleared only when that group becomes route-active again — landing on one of
  its pages reopens it. Opening a folder closes every other one — an accordion
  (2026-09-23; it used to leave the others open). See go(), isGroupOpen and
  activeGroupKey below.

  `isRail` is bound two-way (`v-model:rail`) rather than passed as a bare
  `rail` prop, so its live effective value (true = icon-only, false =
  hover-expanded) is readable here to gate children's visibility.
-->
<template>
  <!-- Desktop: always present, collapsed to an icon rail until the app-bar
       button expands it (no hover auto-expand — Maurizio, 2026-09-09).
       Mobile: hidden until the button opens it, full width, temporary. -->
  <v-navigation-drawer
    :model-value="mobile ? drawer : true"
    :rail="isRail"
    :permanent="!mobile"
    :temporary="mobile"
    disable-route-watcher
    color="grey-darken-4"
    @update:model-value="(v) => { if (mobile) drawer = v; }"
  >
    <v-list density="compact" nav>
      <template v-for="item in visibleNavGroups" :key="item.key">
        <!-- Section label ("Data entry", "Data review", "Modules"): text when the
             drawer is expanded, a thin rule in the icon-only rail so the sections
             still read there. Not clickable. -->
        <!-- The drawer is always dark (color="grey-darken-4"), but a subheader takes
             the THEME's text colour: in the light theme that is black on a dark
             drawer (Maurizio, 2026-09-22: "DATA REVIEW is black"). A fixed light
             grey reads on the drawer whichever theme the page is in. -->
        <v-list-subheader v-if="item.type === 'subheader' && !isRail" :title="item.title" class="text-uppercase text-grey-lighten-1" />
        <!-- A label collapses to a rule in the rail; a "divider" item is a rule in both states. -->
        <v-divider v-else-if="item.type === 'subheader' || item.type === 'divider'" class="my-1" />
        <v-list-item
          v-else
          :title="item.title"
          :subtitle="rowSubtitle(item)"
          :active="isItemActive(item)"
          :append-icon="item.type === 'group' && !isRail && hasVisibleChildren(item) ? (isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right') : undefined"
          @click="go(item)"
        >
          <template v-slot:prepend>
            <v-icon :color="item.iconColor">{{ item.icon }}</v-icon>
          </template>
          <!-- In the icon rail neither title nor subtitle is drawn: give both as a tooltip. -->
          <v-tooltip v-if="isRail" activator="parent" location="end">{{ rowSubtitle(item) ? `${item.title} · ${rowSubtitle(item)}` : item.title }}</v-tooltip>
        </v-list-item>

        <template v-if="item.type === 'group' && !isRail && isGroupOpen(item)">
          <v-list-item
            v-for="child in visibleChildren(item)"
            :key="child.title"
            :title="child.title"
            :subtitle="child.subtitle"
            :active="isItemActive(child)"
            class="ps-4"
            @click="go(child)"
          >
            <template v-slot:prepend>
              <v-icon :color="child.iconColor" size="small">{{ child.icon }}</v-icon>
            </template>
          </v-list-item>
        </template>
      </template>

      <!-- No divider here any more: the "Administration" section label in
           navGroups.js now precedes Users / Audit Logs / Documentation, and
           System Settings below reads as the last item of that section. -->

      <template v-if="isAppAdmin">
        <v-list-item
          :title="settingsGroup.title"
          :active="isItemActive(settingsGroup)"
          :append-icon="!isRail ? (isGroupOpen(settingsGroup) ? 'mdi-chevron-down' : 'mdi-chevron-right') : undefined"
          @click="go(settingsGroup)"
        >
          <template v-slot:prepend><v-icon>{{ settingsGroup.icon }}</v-icon></template>
          <v-tooltip v-if="isRail" activator="parent" location="end">{{ settingsGroup.title }}</v-tooltip>
        </v-list-item>
        <!-- Same open/close rule as every other group (Maurizio, 2026-09-09:
             "system settings doesn't collapse when expanded and clicked"). -->
        <template v-if="!isRail && isGroupOpen(settingsGroup)">
          <v-list-item
            v-for="child in visibleChildren(settingsGroup)"
            :key="child.title"
            :title="child.title"
            :active="isItemActive(child)"
            class="ps-4"
            @click="go(child)"
          >
            <template v-slot:prepend><v-icon size="small">{{ child.icon }}</v-icon></template>
          </v-list-item>
        </template>
      </template>
    </v-list>

    <!-- The drawer's `append` slot, not the end of the main list: it sits
         outside the scrolling content, so the account row and Log out stay
         pinned to the foot of the rail at any window height (Maurizio,
         2026-09-17: "if the win is short it doesn't scroll and is not
         visible"). Verified down to a 380px-tall window. -->
    <template v-slot:append>
      <v-divider></v-divider>
      <v-list density="compact" nav>
        <v-list-item :title="userDisplayName" @click="goToAccount">
          <template v-slot:prepend><v-icon icon="mdi-account"></v-icon></template>
          <template v-slot:subtitle>
            <span class="user-email">{{ user?.email }}</span>
            <v-tooltip activator="parent" location="bottom">{{ user?.email }}</v-tooltip>
          </template>
        </v-list-item>
        <!-- Logging out used to mean knowing it lives INSIDE the Account page,
             reached by clicking your own name — in the icon rail that is one
             unlabelled person icon (Maurizio, 2026-09-17, signed in with the
             wrong account: "the button is missing!"). -->
        <v-list-item title="Log out" @click="logout">
          <template v-slot:prepend><v-icon icon="mdi-logout"></v-icon></template>
          <v-tooltip activator="parent" location="bottom">Log out</v-tooltip>
        </v-list-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";
import { navGroups, settingsGroup, isEntryVisible, isGroupVisible, hasVisibleChildren as hasVisibleChildrenFn } from "@/config/navGroups.js";
import { logout as apiLogout } from "@/utils/api.js";

const { user, drawer, currentApp, appCodes, grantUser } = useApp();
const router = useRouter();
const route = useRoute();
const { mobile } = useDisplay();

// `drawer` (useApp, shared with the app-bar button) now means EXPANDED. On
// desktop the drawer is permanent and merely collapses to an icon rail when
// not expanded; on mobile there is no rail — collapsed means hidden. No
// hover expansion: only the app-bar button (☰ ↔ ✕) changes this.
const isRail = computed(() => !mobile.value && !drawer.value);

// Clicking anywhere outside the expanded drawer collapses it back to the rail
// (Maurizio, 2026-09-10). Capture phase, so it also runs when the page's own
// handlers stop propagation. Clicks inside the drawer (navigating, toggling a
// group) and on the app bar (the ☰/✕ button toggles the drawer itself) are left
// alone — without that exclusion the button's own click would collapse and
// then re-expand in the same tick. Mobile is a temporary drawer with a scrim,
// which Vuetify already closes on outside tap.
function onDocumentClick(e) {
  if (mobile.value || !drawer.value) return;
  const target = e.target;
  if (!(target instanceof Element)) return;
  if (target.closest(".v-navigation-drawer") || target.closest("header")) return;
  drawer.value = false;
}
onMounted(() => document.addEventListener("click", onDocumentClick, true));

// On mobile (below Vuetify's lg breakpoint, 1280px — a laptop window counts)
// the drawer is temporary, and Vuetify closed it on EVERY route change: a click
// on a group header (GL, Sales …) landed on the group's page and hid the drawer
// before the group's entries could be picked (Maurizio, 2026-10-17). Its own
// route watcher is off (disable-route-watcher); this one closes the drawer after
// any navigation except one started by a group header in go().
let keepDrawerOnNextRoute = false;
watch(() => route.fullPath, () => {
  if (mobile.value && !keepDrawerOnNextRoute) drawer.value = false;
  keepDrawerOnNextRoute = false;
});
onBeforeUnmount(() => document.removeEventListener("click", onDocumentClick, true));

const isAppAdmin = computed(() => user.value?.role === "appadmin");
// Context object passed to navGroups.js's visibility helpers: the app codes held
// (to change or only to look — "gl:view" counts as gl) and the person, for the
// edit-only shortcuts and the salary pages (view-only grants, 2026-11-04).
const gateCtx = computed(() => ({ allowedApps: appCodes.value, isAppAdmin: isAppAdmin.value, user: grantUser.value }));

const visibleNavGroups = computed(() => {
  const ctx = gateCtx.value;
  // Separators (section labels and plain dividers) carry no gate of their own.
  const isSeparator = (item) => item.type === "subheader" || item.type === "divider";
  const items = navGroups.filter((item) =>
    isSeparator(item) || (item.type === "group" ? isGroupVisible(item, ctx) : isEntryVisible(item, ctx)),
  );
  // A separator earns its place only if a visible entry follows it before the
  // next separator (or the end) — a kitchen operator with nothing under
  // "Data review" must not see an orphaned heading, and a rule must never sit
  // directly under a label or at the very bottom.
  return items.filter((item, i) => !isSeparator(item) || (items[i + 1] && !isSeparator(items[i + 1])));
});

function visibleChildren(group) {
  return (group.children || []).filter((c) => isEntryVisible(c, gateCtx.value));
}
function hasVisibleChildren(group) {
  return hasVisibleChildrenFn(group, gateCtx.value);
}

// Active-state: a leaf/child is active if the current route name is in its
// routeNames; a group parent is also active when any of its children match
// (so "GL" highlights while viewing Ledger, even in rail/collapsed view).
function isItemActive(item) {
  if (item.routeNames?.includes(route.name)) return true;
  if (item.type === "group") {
    return (item.children || []).some((c) => c.routeNames?.includes(route.name));
  }
  return false;
}

// Which group (if any) contains the current route — that's the only one
// whose children render; every other group stays collapsed to its parent
// row (see file header for why: avoids pushing later items far down).
const activeGroupKey = computed(() => {
  const groups = [...navGroups, settingsGroup].filter((g) => g.type === "group");
  // A group whose OWN page is the current route wins over one that merely
  // lists that page as a child. GL's header is the Journal page and Daily
  // operations lists "Journal entry"; with plain first-match Daily always won,
  // so clicking GL navigated to Journal and expanded Daily instead — GL could
  // never show its children (Maurizio, 2026-09-09).
  const own = groups.find((g) => g.routeNames?.includes(route.name));
  if (own) return own.key;
  const viaChild = groups.find((g) => isItemActive(g));
  return viaChild?.key ?? null;
});

// Path-less groups ("Daily operations", "Monthly operations") are accordions:
// their header toggles the children instead of navigating. Needed because their
// children can be pure actions (the four warehouse dialog shortcuts share the
// Inventory route and carry no routeNames), so route-derived expansion alone
// could never open the group for a kitchen operator. Route-derived expansion
// still applies when one of a group's pages IS the current route. Deliberately
// not reset on navigation: an operator doing Stock Out then Wastage keeps the
// group open between the two.
// Per-group manual override: true = opened by click, false = closed by click.
// A click must be able to CLOSE a group even while you are on one of its pages
// (Maurizio, 2026-09-09: "when I click on an expanded entry it doesn't
// collapse") — so the override wins over the route. It is cleared for a group
// the moment that group becomes the route-active one again (landing on one of
// its pages reopens it), and left alone for every other group, so Daily stays
// open across Stock Out → Wastage while the Warehouse group opens underneath.
const manualOpen = ref({});
// Every group key, for the accordion (opening one closes the rest).
const groupKeys = computed(() => [...navGroups, settingsGroup].filter((g) => g.type === "group").map((g) => g.key));
const isGroupOpen = (group) => manualOpen.value[group.key] ?? (activeGroupKey.value === group.key);
// Landing on one of a group's pages undoes a manual CLOSE of that group, so it
// reopens where you are. A manual OPEN is never touched: groups you opened stay
// open until you close them (Maurizio, 2026-09-10: "click monthly → expand BUT
// do not close daily"). Deleting the open override here, as before, meant that
// once the route stopped holding the group open it silently collapsed.
watch(activeGroupKey, (key) => {
  if (key && manualOpen.value[key] === false) {
    const next = { ...manualOpen.value };
    delete next[key];
    manualOpen.value = next;
  }
});

const userDisplayName = computed(() => {
  const name = [user.value?.first_name, user.value?.last_name].filter(Boolean).join(" ");
  return name || user.value?.email;
});

// Single click handler for every leaf/group-parent/child — navigate, and
// mirror the pre-reorg goTo* handlers' side effect of remembering the last
// visited app (several existing handlers intentionally didn't set this,
// e.g. Documentation/Settings — matched here by simply omitting
// `currentApp` from those entries in navGroups.js).
function go(item) {
  // ONE rule for every group header, with or without a page of its own
  // (Maurizio, 2026-09-09: GL and System Settings expanded but never
  // collapsed — the route kept them open):
  //   collapsed → click expands it, and lands on the group's page if it has
  //               one and you are not already there;
  //   expanded, on the group's own page (or a path-less accordion)
  //             → click collapses it, never navigates;
  //   expanded, elsewhere, and the group HAS a page
  //             → click goes to that page and keeps the group open (Maurizio,
  //               2026-09-10: on Orders, clicking Sales must lead back to Sales
  //               Analytics; before, it only collapsed the group);
  //   from the icon rail → the drawer expands and the group opens (there is
  //               nothing to reveal in the rail, so a toggle would be invisible).
  let target = item; // what a click navigates to: the item itself, or a group's page
  if (item.type === "group") {
    const fromRail = isRail.value;
    if (fromRail) drawer.value = true;
    const open = !fromRail && isGroupOpen(item);
    // A group's page: its own (Warehouse → Inventory) or, for a path-less
    // accordion, its LEAD entry — the first child this user can see (Maurizio,
    // 2026-09-11: "daily ops and monthly ops when clicked don't go to their
    // default page"). Per user, because the entries are gated: Iqbal's Daily
    // operations lands on Delivery, a kitchen operator's on Stock Out, a
    // payroll-only user's Monthly operations on Payroll.
    target = item.path ? item : leadEntry(item);
    // "On its own page" by route NAME first: a header path may redirect
    // (/accounting/sales → /accounting/sales/analytics), so the path alone
    // never matched for Sales and the header kept navigating instead of
    // collapsing. Path equality (query stripped — the dialog shortcuts carry
    // ?out=1 etc.) stays as the fallback for name-less entries.
    const onOwnPage = Boolean(target) && (
      Boolean(target.routeNames?.includes(route.name)) ||
      (Boolean(target.path) && route.path === target.path.split("?")[0])
    );
    if (open && target && !onOwnPage) {
      manualOpen.value = { ...manualOpen.value, [item.key]: true };
    } else if (open) {
      manualOpen.value = { ...manualOpen.value, [item.key]: false };
      return;
    } else {
      // Opening a folder closes every other one — an accordion (Maurizio,
      // 2026-09-23: "if a folder is open it should close, otherwise the menu
      // list is too long"). This reverses 2026-09-10's "do not close daily".
      const next = {};
      for (const g of groupKeys.value) next[g] = false;
      next[item.key] = true;
      manualOpen.value = next;
      if (!target || onOwnPage) return;
    }
    // A header opens its group: on mobile the drawer stays open to pick from it
    keepDrawerOnNextRoute = true;
  }
  // A push that goes nowhere (already there) must not leave the flag set
  router.push(target.path).then((failure) => { if (failure) keepDrawerOnNextRoute = false; });
  if (target.currentApp) {
    currentApp.value = target.currentApp;
    localStorage.setItem("currentApp", JSON.stringify(currentApp.value));
  }
}

/** The first child this user can see — an accordion's page and subtitle. */
function leadEntry(group) {
  return visibleChildren(group)[0] || null;
}

/**
 * Subtitle drawn under a row: the configured one, except that a path-less
 * accordion shows its lead entry's title — "Delivery" for a warehouse admin,
 * "Stock Out" for a kitchen operator — so the label always names where the
 * header actually lands. The configured value is only the fallback.
 */
function rowSubtitle(item) {
  if (item.type === "group" && !item.path) return leadEntry(item)?.title ?? item.subtitle;
  return item.subtitle;
}

/**
 * The shared logout in utils/api.js, not a copy of it: it also stops the
 * token-refresh timer and resets the preferences cache, which a hand-rolled
 * clear here would leave running for the next user on this machine.
 */
const logout = () => apiLogout();

const goToAccount = () => {
  router.push("/account");
};
</script>
